import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';
import i18n from '../i18n';
import { getTodayInTimezone } from '../utils/date';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [currentUser, setCurrentUser] = useState(() => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) return null;
    try {
      const payload = JSON.parse(atob(savedToken.split('.')[1]));
      const savedLastLedgerId = localStorage.getItem(`last_ledger_id_${payload.sub}`) || localStorage.getItem('last_ledger_id');
      return {
        username: payload.sub,
        first_name: payload.first_name || null,
        last_name: payload.last_name || null,
        is_super_admin: payload.is_super_admin,
        last_ledger_id: savedLastLedgerId || null,
        ...payload
      };
    } catch (e) {
      return null;
    }
  });
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('app_language') || 'en');

  const changeLanguage = useCallback((newLang) => {
    if (!newLang) return;
    i18n.changeLanguage(newLang);
    localStorage.setItem('app_language', newLang);
    setCurrentLanguage(newLang);
    if (token) {
      axios.put(`${config.API_URL}/users/me`, { preferred_language: newLang }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => {
        console.error("Failed to persist language preference to backend", err);
      });
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const savedLastLedgerId = localStorage.getItem(`last_ledger_id_${payload.sub}`) || localStorage.getItem('last_ledger_id');
        setCurrentUser(prev => ({
          ...prev,
          username: payload.sub,
          first_name: payload.first_name || prev?.first_name || null,
          last_name: payload.last_name || prev?.last_name || null,
          is_super_admin: payload.is_super_admin,
          last_ledger_id: prev?.last_ledger_id || savedLastLedgerId || null,
          ...payload
        }));
      } catch (e) {
        console.error("Invalid token format");
        setCurrentUser(null);
      }
    } else {
      setCurrentUser(null);
    }
  }, [token]);

  const updateToken = useCallback((newToken) => {
    setToken(newToken);
    if (newToken) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
  }, []);

  const login = async (username, password) => {
    try {
      const params = new URLSearchParams();
      params.append('username', username);
      params.append('password', password);

      const response = await axios.post(`${config.API_URL}/auth/login`, params);

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return response.data; // { requires_2fa, temp_token, methods }
      }

      const { access_token } = response.data;
      updateToken(access_token);
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const logout = () => {
    updateToken('');
  };

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: config.API_URL,
      timeout: 60000,
    });

    instance.interceptors.request.use((reqConfig) => {
      const isSafeUrl =
        typeof reqConfig.url === 'string' &&
        ((reqConfig.url.startsWith('/') && !reqConfig.url.startsWith('//')) ||
         (config.API_URL && reqConfig.url.startsWith(config.API_URL)) ||
         (reqConfig.baseURL && reqConfig.url.startsWith(reqConfig.baseURL)));

      if (token && isSafeUrl) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      if (currentLanguage) {
        reqConfig.headers['Accept-Language'] = currentLanguage;
      }
      return reqConfig;
    });

    instance.interceptors.response.use(
      (response) => {
        // Check if the backend gave us a fresh token
        const refreshedToken = response.headers['x-refreshed-token'];
        if (refreshedToken) {
          updateToken(refreshedToken);
        }
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          updateToken(''); // Clear token -> user logs out -> Redirects to login
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, updateToken, currentLanguage]);

  const refreshUser = useCallback(async () => {
    if (token && api) {
      try {
        const response = await api.get('/users/me');
        const userData = response.data;
        if (userData?.last_ledger_id) {
          if (userData.username) {
            localStorage.setItem(`last_ledger_id_${userData.username}`, userData.last_ledger_id.toString());
          }
          localStorage.setItem('last_ledger_id', userData.last_ledger_id.toString());
        }
        setCurrentUser(prev => ({ ...prev, ...userData }));
        if (userData.preferred_language && userData.preferred_language !== localStorage.getItem('app_language')) {
          i18n.changeLanguage(userData.preferred_language);
          localStorage.setItem('app_language', userData.preferred_language);
          setCurrentLanguage(userData.preferred_language);
        }
      } catch (err) {
        console.error("Failed to refresh user info", err);
      }
    }
  }, [token, api]);

  const preferredCurrency = useMemo(() => currentUser?.preferred_currency || 'CAD', [currentUser]);
  const preferredTimezone = useMemo(() => currentUser?.preferred_timezone || 'America/New_York', [currentUser]);
  const currencySymbol = useMemo(() => preferredCurrency === 'CNY' ? '¥' : '$', [preferredCurrency]);

  const getToday = useCallback(() => {
    return getTodayInTimezone(preferredTimezone);
  }, [preferredTimezone]);

  const formatCurrency = useCallback((amount, currencyCode) => {
    const code = currencyCode || preferredCurrency;
    const symbol = code === 'CNY' ? '¥' : '$';
    const absVal = Math.abs(Number(amount || 0));
    
    const localeMap = {
      'en': 'en-US',
      'fr': 'fr-CA',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
      'es': 'es-ES'
    };
    const activeLocale = localeMap[currentLanguage] || 'en-US';

    const formatted = absVal.toLocaleString(activeLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return Number(amount) >= 0 ? `${symbol}${formatted}` : `(${symbol}${formatted})`;
  }, [preferredCurrency, currentLanguage]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{
      token,
      currentUser,
      setCurrentUser,
      preferredCurrency,
      preferredTimezone,
      currentLanguage,
      changeLanguage,
      getToday,
      currencySymbol,
      formatCurrency,
      refreshUser,
      login,
      logout,
      api,
      setToken: updateToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};